const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const withDefinedValues = (obj) =>
  Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => [key, isPlainObject(value) ? withDefinedValues(value) : value])
      .filter(([, value]) => {
        if (value === undefined) return false;
        if (isPlainObject(value)) return Object.keys(value).length > 0;
        return true;
      })
  );

const getBasicInfo = (application) => application?.basicInfo ?? {};
const getOtherInfo = (application) => application?.otherInfo ?? {};
const getAgencyDetails = (basicInfo) => basicInfo?.agencyDetails ?? {};
const getAgencyId = (agencyId) => {
  if (agencyId === undefined || agencyId === null || agencyId === '' || Number(agencyId) === 0) {
    return undefined;
  }
  return Number(agencyId);
};
const getPayloadAgencyDetails = (values = {}) => {
  const agencyId = getAgencyId(values.agencyId ?? values.agency?.id);
  return agencyId ? { agencyId } : undefined;
};

const getPositionPanelData = (position = {}) => position.panelData ?? position.panel?.panelData;

const getPositionPanelOut = (position = {}) => position.panelOut ?? position.panel?.panelOut;

const getPositionPanelPayload = (values = {}) => {
  if (values.panelData) {
    return withDefinedValues({
      ...values.panel,
      panelData: {
        panel: values.panelData.panel,
      },
      panelOut: values.panelData.panelOut,
    });
  }

  return values.panel;
};

export const normalizeApplication = (application = {}) => {
  if (Array.isArray(application)) return application.map(normalizeApplication);

  const applicationData = application ?? {};
  const basicInfo = getBasicInfo(applicationData);
  const otherInfo = getOtherInfo(applicationData);
  const recruiterInfo = applicationData.recruiterInfo ?? {};
  const referDetails = applicationData.referDetails ?? {};
  const joiningInfo = applicationData.joiningInfo ?? {};
  const lastRoundInfo = applicationData.lastRoundInfo ?? {};
  const rejectionInfo = applicationData.rejectionInfo ?? {};
  const agencyDetails = getAgencyDetails(basicInfo);

  return withDefinedValues({
    ...applicationData,
    id: basicInfo.id,
    name: basicInfo.name,
    applicantID: basicInfo.applicantID ?? basicInfo.applicantId,
    applicantId: basicInfo.applicantID ?? basicInfo.applicantId,
    agencyId: basicInfo.agencyId ?? agencyDetails.agencyId,
    email: basicInfo.email,
    phone: basicInfo.phone,
    status: basicInfo.status,
    updatedStatus: basicInfo.updatedStatus,
    orgID: basicInfo.orgID,
    agency: basicInfo.agency ?? agencyDetails.agency,
    createdAt: otherInfo.createdAt,
    updatedAt: otherInfo.updatedAt,
    versionKey: otherInfo.versionKey,
    isCareer: otherInfo.isCareer,
    isUpload: otherInfo.isUpload,
    coolingPeriod: otherInfo.coolingPeriod,
    uploadedBy: recruiterInfo.uploadedBy,
    recruiter: recruiterInfo.recruiter,
    referrer: referDetails.referrer,
    referrerRelation: referDetails.referrerRelation,
    isReferral: referDetails.isReferral,
    teamID: applicationData.teamID ?? joiningInfo.teamID,
    requiredInfo:
      applicationData.requiredInfo ??
      joiningInfo.requiredInfo ??
      (joiningInfo.joiningDate ? { joiningDate: joiningInfo.joiningDate } : undefined),
    latestRoundID: lastRoundInfo.latestRoundID,
    latestRound: lastRoundInfo.latestRound,
    latestRoundStatus: lastRoundInfo.latestRoundStatus,
    templateID: rejectionInfo.templateID,
    isRejected: rejectionInfo.isRejected,
  });
};

export const normalizeApplications = (applications = []) => applications.map(normalizeApplication);

export const toApplicationPayload = (values = {}) =>
  withDefinedValues({
    basicInfo: withDefinedValues({
      id: values.id,
      applicantID: values.applicantID ?? values.applicantId,
      name: values.name,
      email: values.email,
      phone: values.phone,
      status: values.status,
      updatedStatus: values.updatedStatus,
      orgID: values.orgID,
      agencyDetails: getPayloadAgencyDetails(values),
    }),
    position: values.position,
    customFormData: values.customFormData ?? [],
    otherInfo: withDefinedValues({
      createdAt: values.createdAt,
      updatedAt: values.updatedAt,
      versionKey: values.versionKey,
      isCareer: values.isCareer,
      coolingPeriod: values.coolingPeriod,
      isUpload: values.isUpload,
    }),
    reason: values.reason,
    allowedOperations: values.allowedOperations,
    events: values.events,
    interview: values.interview,
    recruiterInfo: withDefinedValues({
      uploadedBy: values.uploadedBy,
      recruiter: values.recruiter,
    }),
    referDetails: withDefinedValues({
      referrer: values.referrer,
      isReferral: values.isReferral,
      referrerRelation: values.referrerRelation,
    }),
    joiningInfo: withDefinedValues({
      teamID: values.teamID,
      requiredInfo: values.requiredInfo,
      joiningDate: values.requiredInfo?.joiningDate,
    }),
    lastRoundInfo: values.lastRoundInfo,
    rejectionInfo: withDefinedValues({
      templateID: values.templateID,
      isRejected: values.isRejected,
    }),
  });

export const toReferralPayload = (values = {}) =>
  withDefinedValues({
    ...toApplicationPayload(values),
    referDetails: withDefinedValues({
      referrerRelation: values.referrerRelation ?? values.relationship,
      isReferral: true,
    }),
  });

export const normalizePosition = (position = {}) => {
  if (Array.isArray(position)) return position.map(normalizePosition);

  const basic = position.basicDetails ?? position.basic ?? {};
  const details = position.details ?? {};
  const availability = position.availability ?? {};
  const recruitmentTeam = position.recruitmentTeam ?? {};
  const meta = position.meta ?? {};
  const timestamps = meta.timestamps ?? {};
  const templates = position.templates ?? {};
  const panelData = getPositionPanelData(position);
  const panelOut = getPositionPanelOut(position);

  return withDefinedValues({
    ...position,
    id: basic.id,
    title: basic.title,
    hash: basic.hash,
    slug: basic.slug,
    status: basic.status,
    orgID: basic.orgID,
    skill: basic.skill,
    level: basic.level,
    stream: basic.stream,
    skipRounds: basic.skipRounds,
    campusHiringBaseUrl: basic.campusHiringBaseUrl,
    department: details.department,
    description: details.description,
    location: details.location,
    attachments: details.attachments,
    currNoOfOpenings: details.currNoOfOpenings,
    noOfOpenings: details.noOfOpenings,
    noOfJoining: details.noOfJoining,
    sort: details.sort,
    endDate: details.endDate,
    customFormData: details.customFormData,
    isAcceptingApplication: availability.isAcceptingApplication,
    isPublic: availability.isPublic,
    interviewer: recruitmentTeam.hiringManager,
    hr: recruitmentTeam.hr,
    createdAt: timestamps.createdAt,
    updatedAt: timestamps.updatedAt,
    deletedAt: timestamps.deletedAt,
    folderId: meta.folderId,
    access: meta.access,
    count: meta.count,
    versionKey: meta.versionKey,
    allowedOperations: meta.allowedOperations,
    wordCountLimit: meta.wordCountLimit,
    templateID: templates.templateID,
    onlineInviteTemplate: templates.onlineInviteTemplate,
    offlineInviteTemplate: templates.offlineInviteTemplate,
    panelOut,
    panelData: panelData ?? (panelOut ? { panelOut } : undefined),
  });
};

export const normalizePositions = (positions = []) => positions.map(normalizePosition);

export const toPositionPayload = (values = {}) =>
  withDefinedValues({
    basicDetails: withDefinedValues({
      id: values.id,
      title: values.title,
      hash: values.hash,
      slug: values.slug,
      status: values.status,
      orgID: values.orgID,
      skill: values.skill,
      level: values.level,
      stream: values.stream,
      skipRounds: values.skipRounds,
      campusHiringBaseUrl: values.campusHiringBaseUrl,
    }),
    details: withDefinedValues({
      department: values.department,
      description: values.description,
      location: values.location,
      attachments: values.attachments,
      currNoOfOpenings: values.currNoOfOpenings,
      noOfOpenings: values.noOfOpenings,
      noOfJoining: values.noOfJoining,
      sort: values.sort,
      endDate: values.endDate,
      customFormData: values.customFormData,
    }),
    availability: withDefinedValues({
      isAcceptingApplication: values.isAcceptingApplication,
      isPublic: values.isPublic,
    }),
    recruitmentTeam: withDefinedValues({
      hiringManager: values.interviewer,
      hr: values.hr,
    }),
    meta: withDefinedValues({
      versionKey: values.versionKey,
      allowedOperations: values.allowedOperations,
      wordCountLimit: values.wordCountLimit,
      access: values.access,
    }),
    templates: withDefinedValues({
      templateID: values.templateID,
      onlineInviteTemplate: values.onlineInviteTemplate,
      offlineInviteTemplate: values.offlineInviteTemplate,
    }),
    panel: getPositionPanelPayload(values),
    acknowledgmentInformation: values.acknowledgmentInformation,
  });

export const normalizeStream = (stream) => {
  if (stream == null) return stream;
  if (Array.isArray(stream)) return stream.map(normalizeStream);

  const details = stream.details ?? {};
  const timestamps = stream.timestamps ?? {};

  return withDefinedValues({
    ...stream,
    createdAt: timestamps.createdAt,
    updatedAt: timestamps.updatedAt,
    deletedAt: timestamps.deletedAt,
    department: details.department,
    skills: details.skills,
    levels: details.levels,
  });
};

export const normalizeStreams = (streams) => streams?.map(normalizeStream);

export const toStreamPayload = (values = {}) =>
  withDefinedValues({
    id: values.id,
    name: values.name,
    status: values.status,
    versionKey: values.versionKey,
    orgID: values.orgID,
    details: withDefinedValues({
      department: values.department,
      skills: values.skills,
      levels: values.levels,
    }),
    manager: values.manager,
    webHookURL: values.webHookURL,
  });
